import PropTypes from 'prop-types';
import React from 'react';
import { Field } from 'redux-form';

import cls from 'classnames/bind';

import noop from 'lodash/noop';
import _pick from 'lodash/pick';

import Checkbox from '@ott/checkbox';
import FirstErrorShower from '@ott/first-error-shower';
import { l10n, lang } from '@ott/l10n';
import { withLayoutContext } from '@ott/layout-context';
import LoadingDots from '@ott/loading-dots';
import { actions as virtualCardActions } from '@ott/shared-virtual-card-logic';
import Tooltip from '@ott/tooltip';

import AdaptedInput from './blocks/AdaptedForRF6Input';
import CardAutocomplete from './blocks/CardAutocomplete';
import CardCheckIcon from './blocks/CardCheckIcon';
import CardDropdown from './blocks/CardDropdown';
import SmartCardIcon from './blocks/SmartCardIcon';
import ccNumberFormatter from './helpers/ccNumberFormatter';
import flattenReduxFormProps from './helpers/flattenReduxFormProps';
import transforms from './helpers/transforms';
import { withCardCountryCodes } from './hocs/withCardCountryCodes';
import LoadingArrows from './icons/LoadingArrows';
import PlusIcon from './icons/PlusIcon';
import TrashIcon from './icons/TrashIcon';

import styles from './CreditCard.scss';

const CREDIT_CARD_LOCATOR_BASE = 'credit-card';

const CREDIT_CARD_INITIAL = {
  cardNumber: '',
  expMonth: '',
  expYear: '',
  cardCVV: '',
  cardHolder: '',
};

export const CREDIT_CARD_FIELD_NAMES = {
  CARD_HOLDER: 'cardHolder',
  CARD_NUMBER: 'cardNumber',
  CARD_CVV: 'cardCVV',
  CARD_EXP_YEAR: 'expYear',
  CARD_EXP_MONTH: 'expMonth',
  CARD_SAVE: 'saveCard',
  CARD_ID: 'id',
};

const valuesForCardFieldsReset = Object.values(CREDIT_CARD_FIELD_NAMES).reduce(
  (result, fieldName) => {
    result[fieldName] = '';
    return result;
  },
  {}
);

const DOT = '\u2022';

const AnimatedCheckBoxAdapted = ({ meta: { valid, touched, active }, input, ...rest }) => {
  const { value, ...restInput } = input;

  return (
    <Checkbox {...restInput} isChecked={value} hasError={!valid && touched && !active} {...rest} />
  );
};

const CardAutocompleteAdapted = flattenReduxFormProps(CardAutocomplete);

const cx = cls.bind(styles);

let IS_TEXT_SECURITY_SUPPORTED = false;

if (typeof CSS !== 'undefined') {
  IS_TEXT_SECURITY_SUPPORTED =
    CSS?.supports('text-security: disc') || CSS?.supports('-webkit-text-security: disc');
}

@withLayoutContext
class CreditCard extends React.PureComponent {
  constructor(props) {
    super(props);
    this.creditCardRef = React.createRef();
    this.tooltipTimeout = null;
  }

  static defaultProps = {
    title: '',
    mask: DOT,
    buttonText: l10n('paymentCardNew.dropDownButtonText'),
    cvvMaxLength: 4,
    withoutErrorShower: false,
    paymentCardInfo: {
      activeCard: {},
    },
    enableSaveCard: true,
    disableManualInput: false,
    isVirtualCardPayment: false,
    shouldDisableSavedCards: false,
    getSuggests: noop,
    updateActive: noop,
    resetSuggest: noop,
    removeCard: noop,
    onMount: noop,
  };

  static propTypes = {
    cvvMaxLength: PropTypes.number,
    change: PropTypes.func.isRequired,
    isLoadingUserInfo: PropTypes.bool.isRequired,
    title: PropTypes.node,
    buttonText: PropTypes.string.isRequired,
    paymentCardInfo: PropTypes.object.isRequired,
    enableSaveCard: PropTypes.bool,
    disableManualInput: PropTypes.bool,
    getSuggests: PropTypes.func,
    updateActive: PropTypes.func,
    resetSuggest: PropTypes.func,
    removeCard: PropTypes.func,
    submitTime: PropTypes.number,
    isVirtualCardPayment: PropTypes.bool,
    shouldDisableSavedCards: PropTypes.bool,
    onMount: PropTypes.func,
  };

  state = {
    withSuggestState: false,
    withDropdownState: false,
    isTooltipVisible: false,
  };

  componentDidMount() {
    const {
      paymentCardInfo: { activeCard: { id } = {}, activeCard } = {},
      isVirtualCardPayment,
      onMount,
      shouldDisableSavedCards,
    } = this.props;

    const isActiveCardCorrectForCurrentPaymentType =
      (isVirtualCardPayment && activeCard.isVirtualCard) ||
      (!isVirtualCardPayment && !activeCard.isVirtualCard);

    if (id && isActiveCardCorrectForCurrentPaymentType && !shouldDisableSavedCards) {
      this.setCardFieldsFromSuggest({
        ...activeCard,
      });
    }

    onMount();
  }

  getFilteredPaymentCards() {
    const {
      paymentCardInfo: { items: paymentCards },
      isVirtualCardPayment,
      shouldDisableSavedCards,
    } = this.props;

    if (!paymentCards || shouldDisableSavedCards) {
      return [];
    }

    return paymentCards.filter((card) => isVirtualCardPayment || !card.isVirtualCard);
  }

  componentDidUpdate(prevProps) {
    const { withDropdownState, withSuggestState } = this.state;

    const {
      form,
      paymentCardInfo: { items, activeCard },
      isVirtualCardPayment,
      shouldDisableSavedCards,
    } = this.props;

    const paymentCards = items || [];

    const {
      paymentCardInfo: { activeCard: activeCardPrev, items: prevItems },
    } = prevProps;

    const prevPaymentCards = prevItems || [];

    const filteredPaymentCards = this.getFilteredPaymentCards();

    const hasCards = filteredPaymentCards && filteredPaymentCards.length;
    const hasActiveCard = activeCard && activeCard.id;
    const hasActiveCardPrev = activeCardPrev && activeCardPrev.id;

    if (
      isVirtualCardPayment &&
      lang === 'ru' &&
      (!activeCard.isVirtualCard || paymentCards.length !== prevPaymentCards.length)
    ) {
      const virtualCard = paymentCards.find((card) => card.isVirtualCard);

      if (virtualCard) {
        this.handleSelectCard(virtualCard);
      }
    }

    if (
      !isVirtualCardPayment &&
      (activeCard.isVirtualCard || paymentCards.length !== prevPaymentCards.length)
    ) {
      const notVirtualCard =
        paymentCards.find((card) => !card.isVirtualCard) || CREDIT_CARD_INITIAL;

      this.handleSelectCard(notVirtualCard);
    }

    if (form && hasActiveCard && !hasActiveCardPrev && !shouldDisableSavedCards) {
      this.setCardFieldsFromSuggest({
        ...activeCard,
      });
    }

    // todo заменить на getDerivedStateToProps, когда в отелях будет React16
    if (hasCards && !withDropdownState && !withSuggestState) {
      this.setState({
        withDropdownState: true,
        withSuggestState: false,
      });
    }

    if (!hasCards && (withDropdownState || withSuggestState)) {
      this.setState({
        withDropdownState: false,
        withSuggestState: false,
      });

      // после выхода из аккаунта
      if (hasActiveCardPrev) {
        this.imperativeChangeCardFields(CREDIT_CARD_INITIAL);
      }
    }
  }

  imperativeChangeCardFields = (newValuesForFields) => {
    const { change, initialize, form } = this.props;

    if (form) {
      initialize(newValuesForFields);
    } else {
      const { id, ...fields } = newValuesForFields || {};

      // обязательно начинаем изменения с id
      change('id', id);
      Object.keys(fields).forEach((fieldName) => {
        change(fieldName, fields[fieldName]);
      });
    }
  };

  setCardFieldsFromSuggest = (suggestedCardValues) => {
    const onlyFormFieldValues = _pick(suggestedCardValues, Object.values(CREDIT_CARD_FIELD_NAMES));

    this.imperativeChangeCardFields({
      expMonth: '',
      expYear: '',
      cardCVV: '',
      ...onlyFormFieldValues,
    });
  };

  getActiveCardIndex = (activeCardObj, items) =>
    activeCardObj && items.findIndex((cardItem) => cardItem.id === activeCardObj.id);

  handleGetCvcClick = () => {
    const { dispatch } = this.props;

    this.setState({
      isTooltipVisible: true,
    });

    this.tooltipTimeout = setTimeout(() => {
      this.setState({
        isTooltipVisible: false,
      });
    }, 3000);

    dispatch(virtualCardActions.getVerificationCode());
  };

  handleSelectCard = (activeCardObj) => {
    const { updateActive, saveCardFieldValue } = this.props;

    updateActive(activeCardObj);

    this.setCardFieldsFromSuggest({
      saveCard: saveCardFieldValue,
      ...activeCardObj,
    });

    if (this.state.withSuggestState) {
      this.setState({
        withDropdownState: true,
      });
    }
  };

  handleChangeSuggest = (value) => {
    const { updateActive, getSuggests } = this.props;
    updateActive(value);
    getSuggests(value);
  };

  changeCardState = () => {
    this.setState({
      withSuggestState: true,
      withDropdownState: false,
    });

    const { resetSuggest, saveCardFieldValue } = this.props;

    resetSuggest();

    this.imperativeChangeCardFields({
      ...valuesForCardFieldsReset,
      saveCard: saveCardFieldValue,
    });
  };

  handleInputChange = (value) => {
    const { updateActive } = this.props;

    updateActive(value);

    return value;
  };

  setFocusForField(value, checkLength, fieldName) {
    if (value.length >= checkLength) {
      setTimeout(() => {
        const nextField = this.creditCardRef?.current.querySelector(`[name="${fieldName}"]`);
        if (nextField) {
          nextField.focus();
        }
      });
    }
  }

  onMonthChange = (value) => {
    this.setFocusForField(value, 2, CREDIT_CARD_FIELD_NAMES.CARD_EXP_YEAR);

    return value;
  };

  onYearChange = (value) => {
    this.setFocusForField(value, 2, CREDIT_CARD_FIELD_NAMES.CARD_CVV);

    return value;
  };

  onCvvChange = (value) => {
    const {
      paymentCardInfo: {
        activeCard: { cvvMaxLength },
      },
    } = this.props;

    if (value.length === (cvvMaxLength || 4)) {
      this.setFocusForField(value, 3, CREDIT_CARD_FIELD_NAMES.CARD_HOLDER);
    }

    return value;
  };

  renderIconNode = (type, expiredCardId, isLoading) => {
    const { removeCard } = this.props;

    if (!expiredCardId) {
      return <SmartCardIcon type={type} className={styles.cardIcon} />;
    }

    const clickHandler = (e) => {
      e.preventDefault();
      e.stopPropagation();

      removeCard(expiredCardId);
    };

    if (isLoading) {
      return (
        <LoadingArrows w="34px" h="24px" className={cls(styles.cardIcon, styles.loadingIcon)} />
      );
    }

    return (
      <TrashIcon
        w="34px"
        h="24px"
        className={styles.cardIcon}
        style={{ cursor: 'pointer' }}
        onMouseUp={clickHandler}
        onTouchStart={clickHandler}
      />
    );
  };

  renderItemNode = (item, isActive, isExpired) => {
    const { expired, expiresSoon, expirationDate, isRemoving } = item;
    const expiredCardId = isExpired && (expired || expiresSoon) && item.id;

    return (
      <div className={cls(styles.option, expired ? styles.disabled : '')}>
        <span className={expired ? styles.disabled : ''}>{item.cardNumber}</span>
        {isActive && <CardCheckIcon style={{ marginLeft: 10 }} />}
        {isExpired && (
          <span className={expirationDate ? styles.expirationDate : ''}>{expirationDate}</span>
        )}
        <div className={styles.optionIcon}>
          {this.renderIconNode(item.issuingNetwork, expiredCardId, isRemoving)}
        </div>
      </div>
    );
  };

  renderOption = (item, ...args) => {
    const [index, props] = args;
    const { items = [], initialValue } = props;
    const isActiveIcon = index === this.getActiveCardIndex(initialValue, items);

    return this.renderItemNode(item, isActiveIcon, true);
  };

  renderCardDropdown() {
    const {
      isTouchDevice,
      disableManualInput,
      paymentCardInfo: { items: paymentCards, activeCard },
      isVirtualCardPayment,
    } = this.props;

    const validCards = paymentCards.filter(
      (card) => !card.expired && !card.isVirtualCard && !isVirtualCardPayment
    );

    const filteredPaymentCards = paymentCards.filter((card) =>
      isVirtualCardPayment && lang === 'ru' ? true : !card.isVirtualCard
    );

    return (
      <CardDropdown
        label={l10n('paymentCardNew.numberPlaceholder')}
        renderTriggerValue={this.renderItem}
        renderOption={this.renderOption}
        onOptionSelect={this.handleSelectCard}
        withBorder={true}
        withArrow={false}
        isTouchDevice={isTouchDevice}
        items={filteredPaymentCards}
        disabled={isVirtualCardPayment && lang === 'ru'}
        initialValue={activeCard}
        initialActiveIdx={filteredPaymentCards.findIndex((card) => card.id === activeCard.id)}
        autoSelectIfOne={Boolean(validCards.length)}
        optionalButtonHandler={this.changeCardState}
        buttonContent={this.renderButtonContent()}
        disableManualInput={disableManualInput}
      />
    );
  }

  renderAutoComplete() {
    const {
      isTouchDevice,
      paymentCardInfo: {
        suggests,
        activeCard: { issuingNetwork },
      },
      isVirtualCardPayment,
    } = this.props;

    const iconNode = this.renderIconNode(issuingNetwork);

    const formatProps = {
      formatter: ccNumberFormatter,
      parse: transforms.number.parse,
    };

    const filteredSuggests = suggests.filter((suggest) => !suggest.isVirtualCard);

    return (
      <Field
        component={CardAutocompleteAdapted}
        name={CREDIT_CARD_FIELD_NAMES.CARD_NUMBER}
        placeholder={l10n('paymentCardNew.numberPlaceholder')}
        props={{
          ...formatProps,
          onText: this.handleChangeSuggest,
          getOptionKey: this.getSuggestKey,
          renderOption: this.renderAutocompleteItem,
          items: filteredSuggests,
          icon: iconNode,
          inputMode: 'decimal',
          isTouchDevice,
          withArrow: false,
          disabled: isVirtualCardPayment,
          formNoValidate: true,
          onOptionSelect: this.handleSelectCard,
          enableAutoComplete: true,
          autoCompleteName: 'cc-number',
        }}
      />
    );
  }

  renderAutocompleteItem = (item) => {
    return this.renderItemNode(item, false, true);
  };

  renderItem = (item) => {
    return this.renderItemNode(item, false, false);
  };

  renderButtonContent() {
    const { buttonText } = this.props;

    return (
      <span className={styles.content}>
        <span className={styles.text}>{buttonText}</span>
        <PlusIcon className={styles.icon} />
      </span>
    );
  }

  renderCardInput() {
    const {
      paymentCardInfo: {
        activeCard: { issuingNetwork, id },
      },
      isVirtualCardPayment,
    } = this.props;

    // нужно, т к для сохраненной карты приходит отформатированное значение
    const isSavedCard = Boolean(id);
    const cardIcon = this.renderIconNode(issuingNetwork);

    return (
      <Field
        component={AdaptedInput}
        name={CREDIT_CARD_FIELD_NAMES.CARD_NUMBER}
        placeholder={l10n('paymentCardNew.numberPlaceholder')}
        formNoValidate={true}
        props={{
          parse: isSavedCard ? null : transforms.number.parse,
          formatter: isSavedCard ? null : ccNumberFormatter,
          inputMode: 'decimal',
          icon: cardIcon,
          onChange: this.handleInputChange,
          disabled: isVirtualCardPayment,
          enableAutoComplete: true,
          autoCompleteName: 'cc-number',
        }}
      />
    );
  }

  renderFormControl() {
    const { withDropdownState, withSuggestState } = this.state;

    if (withDropdownState) {
      return this.renderCardDropdown();
    }

    if (withSuggestState) {
      return this.renderAutoComplete();
    }

    return this.renderCardInput();
  }

  renderSaveCardCheckbox() {
    const { isInactive, enableSaveCard } = this.props;

    const { withDropdownState } = this.state;

    if (withDropdownState || !enableSaveCard) {
      return null;
    }

    return (
      <div className={cx('row--checkbox')}>
        <Field
          name={CREDIT_CARD_FIELD_NAMES.CARD_SAVE}
          component={AnimatedCheckBoxAdapted}
          props={{
            isDisabled: isInactive,
            children: l10n('paymentCardNew.saveCardData'),
            specificLocators: 'save-card-data',
          }}
        />
      </div>
    );
  }

  render() {
    const {
      className,
      containerClassName,
      title,
      isLoadingUserInfo,
      withoutErrorShower,
      errors,
      submitTime,
      submitFailed,
      paymentCardInfo: { activeCard: { id, cvvMaxLength, isVirtualCard } = {} },
      isVirtualCardPayment,
      mask,
      isInactive,
      disableManualInput,
    } = this.props;

    const isSavedCard = Boolean(id);
    const dataMask = isSavedCard ? `${mask}${mask}` : null;

    const { withDropdownState, isTooltipVisible } = this.state;

    const saveCardCheckboxNode = this.renderSaveCardCheckbox();

    const titleNode = Boolean(title) && <div className={cx('title')}>{title}</div>;

    const overlayNode = isLoadingUserInfo ? (
      <div className={cx('cardOverlay')}>
        <LoadingDots theme="credit-card" />
      </div>
    ) : null;

    let disabledCardNode;
    if (isInactive) {
      disabledCardNode = <div className={cx('cardOverlay')} />;
    }

    return (
      <div
        className={cx('CreditCard', className)}
        data-locator={`${CREDIT_CARD_LOCATOR_BASE}`}
        ref={this.creditCardRef}
      >
        {!withoutErrorShower && (
          <FirstErrorShower
            errors={errors}
            time={submitTime}
            show={submitFailed}
            container={this}
          />
        )}
        <div
          className={cx('form', {
            'form--transparent': isLoadingUserInfo || isInactive,
          })}
        >
          {overlayNode}
          {disabledCardNode}

          <div className={cx('container', containerClassName)}>
            {titleNode}
            <div className={cx('row')}>
              <div className={cx('wrap')} data-locator={`${CREDIT_CARD_LOCATOR_BASE}__card-number`}>
                {this.renderFormControl()}
              </div>
            </div>

            <div className={cx('row', 'row--cvv')}>
              <div
                className={cx('monthDateWrapper')}
                data-locator={`${CREDIT_CARD_LOCATOR_BASE}__month`}
              >
                <Field
                  component={AdaptedInput}
                  name={CREDIT_CARD_FIELD_NAMES.CARD_EXP_MONTH}
                  props={{
                    ...(withDropdownState ? {} : transforms.expMonth),
                    disabled: disableManualInput || withDropdownState || isVirtualCardPayment,
                    'data-mask': dataMask,
                    enableAutoComplete: !withDropdownState,
                    autoCompleteName: 'cc-exp-month',
                    inputMode: 'decimal',
                    onChange: this.onMonthChange,
                  }}
                  placeholder={l10n('paymentCardNew.monthPlaceholder')}
                />
              </div>
              <div
                className={cx('monthDateWrapper')}
                data-locator={`${CREDIT_CARD_LOCATOR_BASE}__year`}
              >
                <Field
                  component={AdaptedInput}
                  name={CREDIT_CARD_FIELD_NAMES.CARD_EXP_YEAR}
                  props={{
                    ...(withDropdownState ? {} : transforms.expYear),
                    placeholder: l10n('paymentCardNew.yearPlaceholder'),
                    disabled: disableManualInput || withDropdownState || isVirtualCardPayment,
                    'data-mask': dataMask,
                    enableAutoComplete: !withDropdownState,
                    autoCompleteName: 'cc-exp-year',
                    inputMode: 'decimal',
                    onChange: this.onYearChange,
                  }}
                />
              </div>
              <div className={cx('cvvWrapper')} data-locator={`${CREDIT_CARD_LOCATOR_BASE}__cvv`}>
                <Field
                  placeholder={l10n('paymentCardNew.cvvPlaceholder')}
                  component={AdaptedInput}
                  type={IS_TEXT_SECURITY_SUPPORTED ? '' : 'password'}
                  name={CREDIT_CARD_FIELD_NAMES.CARD_CVV}
                  props={{
                    ...transforms.cvv,
                    enableAutoComplete: !withDropdownState,
                    autoCompleteName: 'cc-csc',
                    onChange: this.onCvvChange,
                    isCVV: true,
                    inputMode: 'decimal',
                    maxLength: cvvMaxLength,
                  }}
                />
              </div>
            </div>
            {isVirtualCard && isVirtualCardPayment && (
              <div className={cx('getCvv')}>
                <Tooltip
                  isVisible={isTooltipVisible}
                  content={<span>CVC-код отправлен в смс</span>}
                >
                  <span onClick={this.handleGetCvcClick}>Получить CVC-код в СМС</span>
                </Tooltip>
              </div>
            )}
            <div className={cx('row')}>
              <div
                className={cx('wrap')}
                data-locator={`${CREDIT_CARD_LOCATOR_BASE}__cardholder-name`}
              >
                <Field
                  placeholder={l10n('paymentCardNew.namePlaceholder')}
                  component={AdaptedInput}
                  name={CREDIT_CARD_FIELD_NAMES.CARD_HOLDER}
                  props={{
                    ...transforms.holderName,
                    modificators: ['isUpperCase'],
                    disabled: disableManualInput || withDropdownState || isVirtualCardPayment,
                    enableAutoComplete: !withDropdownState,
                    autoCompleteName: 'cc-name',
                  }}
                />
              </div>
            </div>
          </div>
        </div>
        {saveCardCheckboxNode}
      </div>
    );
  }
}

export default (props) => {
  const { excludeCardCountryCodes = [] } = props;
  if (excludeCardCountryCodes.length) {
    return withCardCountryCodes(CreditCard)(props);
  }

  return <CreditCard {...props} />;
};
